import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../supabaseClient'

function ManageLures() {
  const [lures, setLures] = useState([])
  const [brands, setBrands] = useState([])
  const [selectedBrand, setSelectedBrand] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    fetchBrands()
    fetchLures()
  }, [])

  useEffect(() => {
    fetchLures()
  }, [selectedBrand])

  const fetchBrands = async () => {
    try {
      const { data } = await supabase
        .from('brands')
        .select('brand_id, brand_name')
        .order('brand_name')

      setBrands(data || [])
    } catch (err) {
      console.error('Error fetching brands:', err)
    }
  }

  const fetchLures = async () => {
    try {
      let query = supabase
        .from('base_lures')
        .select(`
          *,
          brands (brand_name),
          lure_types (type_name),
          lure_variants (variant_id)
        `)
        .order('name')

      if (selectedBrand) {
        query = query.eq('brand_id', selectedBrand)
      }

      const { data } = await query

      // Add variant count
      const processedLures = data?.map(lure => ({
        ...lure,
        variantCount: lure.lure_variants?.length || 0
      })) || []

      setLures(processedLures)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching lures:', err)
      setError('Failed to load lures')
      setLoading(false)
    }
  }

  const handleDelete = async (lure) => {
    if (!window.confirm(`Delete "${lure.name}"? This will delete all ${lure.variantCount} color variants too.`)) {
      return
    }

    try {
      // Delete variants first (cascade should handle this, but being explicit)
      const { error: variantError } = await supabase
        .from('lure_variants')
        .delete()
        .eq('base_lure_id', lure.base_lure_id)

      if (variantError) throw variantError

      // Delete base lure
      const { error: lureError } = await supabase
        .from('base_lures')
        .delete()
        .eq('base_lure_id', lure.base_lure_id)

      if (lureError) throw lureError

      setSuccess(`"${lure.name}" deleted successfully!`)
      fetchLures()
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error deleting lure:', err)
      setError(err.message || 'Failed to delete lure')
    }
  }

  if (loading) {
    return <div className="loading">Loading lures...</div>
  }

  return (
    <div className="admin-form-page">
      <h1>Manage Lures</h1>
      <p className="page-subtitle">Edit or delete existing lures</p>

      {error && <div className="error-message">❌ {error}</div>}
      {success && <div className="success-message">✅ {success}</div>}

      {/* Filter by Brand */}
      <div className="admin-section">
        <div className="form-group">
          <label htmlFor="brand-filter">Filter by Brand:</label>
          <select
            id="brand-filter"
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="form-select"
          >
            <option value="">All Brands</option>
            {brands.map(brand => (
              <option key={brand.brand_id} value={brand.brand_id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Lures List */}
      <div className="lures-management-section">
        <div className="section-header">
          <h2>Lures ({lures.length})</h2>
        </div>

        {lures.length === 0 ? (
          <div className="no-lures-message">
            <p>No lures found.</p>
            <Link to="/admin/add-lure" className="btn-primary">
              Add First Lure
            </Link>
          </div>
        ) : (
          <div className="lures-management-grid">
            {lures.map(lure => (
              <div key={lure.base_lure_id} className="lure-management-card">
                <div className="lure-card-header">
                  <h3>{lure.name}</h3>
                  <span className="variant-count-small">
                    {lure.variantCount} colors
                  </span>
                </div>

                <div className="lure-card-details">
                  <p><strong>Brand:</strong> {lure.brands?.brand_name}</p>
                  <p><strong>Type:</strong> {lure.lure_types?.type_name}</p>
                  {lure.weight && <p><strong>Weight:</strong> {lure.weight}</p>}
                  {lure.length && <p><strong>Length:</strong> {lure.length}</p>}
                  {lure.depth_range && <p><strong>Depth:</strong> {lure.depth_range}</p>}
                </div>

                <div className="lure-card-actions">
                  <Link
                    to={`/admin/edit-lure/${lure.base_lure_id}`}
                    className="btn-edit btn-full"
                  >
                    ✏️ Edit Details
                  </Link>
                  <Link
                    to={`/admin/manage-variants/${lure.base_lure_id}`}
                    className="btn-secondary btn-full"
                  >
                    🎨 Manage Colors ({lure.variantCount})
                  </Link>
                  <button
                    onClick={() => handleDelete(lure)}
                    className="btn-delete btn-full"
                  >
                    🗑️ Delete Lure
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ManageLures